import { useState, useCallback } from 'react';
import { Button } from '@ontruck/orc';
import moment from 'moment';

import { editDateMonthAndYearFromMoment, editHourAndMinuteFromString } from './utils';

import DayField from './DayField';
import TimeField from './TimeField';

const LAST_MODIFIED = {
  timeFrom: 'time_from',
  timeTo: 'time_to',
};

const EditTimesTooltipContent = ({ timeFrom, timeTo, onSave }) => {
  const [lastModified, setLastModified] = useState(null);
  const [modifiedTimeFrom, setModifiedTimeFrom] = useState(timeFrom);
  const [modifiedTimeTo, setModifiedTimeTo] = useState(timeTo);

  const timeFromMoment = moment(modifiedTimeFrom);
  const timeToMoment = moment(modifiedTimeTo);

  const isValid = timeToMoment.isSameOrAfter(timeFromMoment);
  const hasError = !isValid;

  const handleStartDayOnChange = useCallback(
    (momentDateTime) => {
      const newModifiedTimeFrom = editDateMonthAndYearFromMoment(momentDateTime, modifiedTimeFrom);
      const newModifiedTimeTo = editDateMonthAndYearFromMoment(momentDateTime, modifiedTimeTo);
      setModifiedTimeFrom(newModifiedTimeFrom);
      setModifiedTimeTo(newModifiedTimeTo);
      setLastModified(LAST_MODIFIED.timeFrom);
    },
    [modifiedTimeFrom, modifiedTimeTo]
  );

  const handleStartTimeOnChange = useCallback(
    ({ hour, minute }) => {
      const newModifiedTimeFrom = editHourAndMinuteFromString(modifiedTimeFrom, { hour, minute });
      setModifiedTimeFrom(newModifiedTimeFrom);
      setLastModified(LAST_MODIFIED.timeFrom);
    },
    [modifiedTimeFrom]
  );

  const handleEndDayOnChange = useCallback(
    (momentDateTime) => {
      const newModifiedTimeTo = editDateMonthAndYearFromMoment(momentDateTime, modifiedTimeTo);
      setModifiedTimeTo(newModifiedTimeTo);
      setLastModified(LAST_MODIFIED.timeTo);
    },
    [modifiedTimeTo]
  );

  const handleEndTimeOnChange = useCallback(
    ({ hour, minute }) => {
      const newModifiedTimeTo = editHourAndMinuteFromString(modifiedTimeTo, { hour, minute });
      setModifiedTimeTo(newModifiedTimeTo);
      setLastModified(LAST_MODIFIED.timeTo);
    },
    [modifiedTimeTo]
  );

  const handleSave = useCallback(() => {
    onSave({
      timeFrom: modifiedTimeFrom,
      timeTo: modifiedTimeTo,
    });
  }, [onSave, modifiedTimeFrom, modifiedTimeTo]);

  return (
    <div className="content">
      <div className="field-group">
        <div className="field">
          <DayField
            label="Start day"
            value={modifiedTimeFrom}
            onChange={handleStartDayOnChange}
            hasError={hasError && lastModified === LAST_MODIFIED.timeFrom}
            nameKey="from"
          />
        </div>
        <div className="field">
          <DayField
            label="End day"
            value={modifiedTimeTo}
            onChange={handleEndDayOnChange}
            hasError={hasError && lastModified === LAST_MODIFIED.timeTo}
            nameKey="to"
          />
        </div>
      </div>
      <div className="field-group">
        <div className="field">
          <TimeField
            label="Start time"
            value={modifiedTimeFrom}
            onChange={handleStartTimeOnChange}
            hasError={hasError && lastModified === LAST_MODIFIED.timeFrom}
            nameKey="from"
          />
        </div>
        <div className="field">
          <TimeField
            label="End time"
            value={modifiedTimeTo}
            onChange={handleEndTimeOnChange}
            hasError={hasError && lastModified === LAST_MODIFIED.timeTo}
            nameKey="to"
          />
        </div>
      </div>
      <div className="button-group">
        <Button
          onClick={handleSave}
          disabled={!isValid}
          skin="primary"
          icon="check"
          iconPosition="left"
          data-testid="SubmitButton"
        />
      </div>
    </div>
  );
};

export default EditTimesTooltipContent;
